import { request } from '../utils/Api';

// Get conversations
export function getConversations(accessToken) {
  return dispatch =>
    new Promise((resolve, reject) => {
      dispatch({
        type: 'CONVERSATIONS_LOADING',
      });

      request(
        'conversations',
        'GET',
        {},
        {
          Authorization: `Bearer ${accessToken}`,
        },
      )
        .then(response => {
          if (response.status === 200) {
            dispatch({
              type: 'CONVERSATIONS_LOADED',
              payload: response.data,
            });
            resolve(response.data);
          } else {
            dispatch({
              type: 'CONVERSATIONS_ERROR',
              payload: response.message,
            });
            reject(response);
          }
        })
        .catch(error => {
          dispatch({
            type: 'CONVERSATIONS_ERROR',
            payload: error.message,
          });
          reject(error);
        });
    });
}

// Ensure a conversation exists for a given match and return it
export function ensureConversationForMatch(matchId, accessToken) {
  return dispatch =>
    new Promise((resolve, reject) => {
      if (!matchId || !accessToken) {
        const error = new Error('Match ID and access token are required');
        reject(error);
        return;
      }

      dispatch({
        type: 'ENSURE_CONVERSATION_REQUEST',
        payload: {
          matchId,
        },
      });

      request(`match/${matchId}/conversation`, 'GET', null, {
        Authorization: `Bearer ${accessToken}`,
      })
        .then(response => {
          if (response.status === 200 && response.data && response.data.conversation) {
            const conversation = response.data.conversation;

            dispatch({
              type: 'ENSURE_CONVERSATION_SUCCESS',
              payload: {
                conversation,
              },
            });

            resolve(conversation);
          } else {
            const errorMessage =
              (response.data && response.data.error) || 'Unable to create conversation';

            dispatch({
              type: 'ENSURE_CONVERSATION_ERROR',
              payload: {
                matchId,
                error: errorMessage,
              },
            });

            reject(
              response instanceof Error ? response : new Error(errorMessage || 'Request failed'),
            );
          }
        })
        .catch(error => {
          const message =
            (error && error.data && error.data.error) || error.message || 'Request failed';

          dispatch({
            type: 'ENSURE_CONVERSATION_ERROR',
            payload: {
              matchId,
              error: message,
            },
          });

          reject(error);
        });
    });
}

// Get messages for a conversation
export function getMessages(conversationId, page = 1, pageSize = 20, accessToken) {
  return dispatch =>
    new Promise((resolve, reject) => {
      if (!conversationId) {
        dispatch({
          type: 'MESSAGES_ERROR',
          payload: {
            conversationId,
            error: 'Conversation ID is required',
          },
        });
        reject(new Error('Conversation ID is required'));
        return;
      }

      dispatch({
        type: 'MESSAGES_LOADING',
        payload: { conversationId },
      });

      request(
        `messages/${conversationId}/conversation`,
        'GET',
        {
          page,
          pageSize,
        },
        {
          Authorization: `Bearer ${accessToken}`,
        },
      )
        .then(response => {
          if (response.status === 200) {
            dispatch({
              type: 'MESSAGES_LOADED',
              payload: {
                conversationId,
                data: response.data,
              },
            });
            resolve(response.data);
          } else {
            dispatch({
              type: 'MESSAGES_ERROR',
              payload: {
                conversationId,
                error: response.message,
              },
            });
            reject(response);
          }
        })
        .catch(error => {
          dispatch({
            type: 'MESSAGES_ERROR',
            payload: {
              conversationId,
              error: error.message,
            },
          });
          reject(error);
        });
    });
}

// Send a message
export function sendMessage(
  conversationId,
  content,
  accessToken,
  currentUserId,
  attachmentIds = [],
) {
  return dispatch =>
    new Promise((resolve, reject) => {
      // Create a temporary ID for the optimistic message
      const tempId = `temp-${Date.now()}-${Math.random()}`;

      // Create an optimistic message object
      const optimisticMessage = {
        id: tempId,
        content,
        sender: {
          id: currentUserId,
        },
        created: new Date().toISOString(),
        isRead: true,
        isPending: true, // Flag to indicate this is an optimistic update
        attachments: [], // Empty for optimistic update, will be populated on success
      };

      dispatch({
        type: 'SEND_MESSAGE_PENDING',
        payload: {
          conversationId,
          content,
          optimisticMessage,
        },
      });

      const requestBody = {
        content,
      };

      // Only add attachmentIds if there are any
      if (attachmentIds && attachmentIds.length > 0) {
        requestBody.attachmentIds = attachmentIds;
      }

      request(`messages/${conversationId}`, 'POST', requestBody, {
        Authorization: `Bearer ${accessToken}`,
      })
        .then(response => {
          if (response.status === 200) {
            dispatch({
              type: 'SEND_MESSAGE_SUCCESS',
              payload: {
                conversationId,
                message: response.data.data,
                tempId, // Include tempId to replace the optimistic message
              },
            });
            resolve(response.data);
          } else {
            dispatch({
              type: 'SEND_MESSAGE_ERROR',
              payload: {
                conversationId,
                error: response.message,
                tempId, // Include tempId to remove the failed message
              },
            });
            reject(response);
          }
        })
        .catch(error => {
          dispatch({
            type: 'SEND_MESSAGE_ERROR',
            payload: {
              conversationId,
              error: error.message,
              tempId, // Include tempId to remove the failed message
            },
          });
          reject(error);
        });
    });
}

// Delete a message
export function deleteMessage(messageId, accessToken) {
  return dispatch =>
    new Promise((resolve, reject) => {
      dispatch({
        type: 'DELETE_MESSAGE_PENDING',
        payload: {
          messageId,
        },
      });

      request(
        `messages/${messageId}`,
        'DELETE',
        {},
        {
          Authorization: `Bearer ${accessToken}`,
        },
      )
        .then(response => {
          if (response.status === 200) {
            dispatch({
              type: 'DELETE_MESSAGE_SUCCESS',
              payload: {
                messageId,
              },
            });
            resolve(response.data);
          } else {
            dispatch({
              type: 'DELETE_MESSAGE_ERROR',
              payload: {
                messageId,
                error: response.message,
              },
            });
            reject(response);
          }
        })
        .catch(error => {
          dispatch({
            type: 'DELETE_MESSAGE_ERROR',
            payload: {
              messageId,
              error: error.message,
            },
          });
          reject(error);
        });
    });
}

// Report a message
export function reportMessage(messageId, reason, accessToken) {
  return dispatch =>
    new Promise((resolve, reject) => {
      dispatch({
        type: 'REPORT_MESSAGE_PENDING',
        payload: {
          messageId,
          reason,
        },
      });

      request(
        `messages/${messageId}/report`,
        'POST',
        {
          reason,
        },
        {
          Authorization: `Bearer ${accessToken}`,
        },
      )
        .then(response => {
          if (response.status === 200) {
            dispatch({
              type: 'REPORT_MESSAGE_SUCCESS',
              payload: {
                messageId,
                report: response.data.data,
              },
            });
            resolve(response.data);
          } else {
            dispatch({
              type: 'REPORT_MESSAGE_ERROR',
              payload: {
                messageId,
                error: response.message,
              },
            });
            reject(response);
          }
        })
        .catch(error => {
          dispatch({
            type: 'REPORT_MESSAGE_ERROR',
            payload: {
              messageId,
              error: error.message,
            },
          });
          reject(error);
        });
    });
}

// Update messaging state
export function updateMessagingState(fields) {
  return {
    type: 'UPDATE_MESSAGING_STATE',
    payload: fields,
  };
}

// Get unread message count
export function getUnreadMessageCount(accessToken) {
  return dispatch =>
    new Promise((resolve, reject) => {
      request(
        'conversations/unread-count',
        'GET',
        {},
        {
          Authorization: `Bearer ${accessToken}`,
        },
      )
        .then(response => {
          if (response.status === 200) {
            dispatch({
              type: 'SET_UNREAD_MESSAGE_COUNT',
              payload: response.data.unreadCount,
            });
            resolve(response.data.unreadCount);
          } else {
            reject(response);
          }
        })
        .catch(error => {
          reject(error);
        });
    });
}

// Optimistically mark a conversation as read in local state
export function markConversationAsRead(conversationId) {
  return (dispatch, getState) =>
    new Promise(resolve => {
      const state = getState();
      const conversationIdNumber = parseInt(conversationId, 10);

      if (Number.isNaN(conversationIdNumber)) {
        resolve(0);
        return;
      }

      const conversationsState = state.messaging.conversations || {};
      const conversationsData = conversationsState.data || [];

      const matchingConversation = conversationsData.find(
        conversation => conversation && conversation.id === conversationIdNumber,
      );

      const unreadDelta =
        matchingConversation && typeof matchingConversation.unreadCount === 'number'
          ? matchingConversation.unreadCount
          : 0;

      dispatch({
        type: 'MARK_CONVERSATION_READ',
        payload: {
          conversationId: conversationIdNumber,
          unreadDelta,
        },
      });

      resolve(unreadDelta);
    });
}

// End conversation and unfriend
export function endConversation(conversationId, accessToken) {
  return dispatch =>
    new Promise((resolve, reject) => {
      dispatch({
        type: 'END_CONVERSATION_PENDING',
        payload: { conversationId },
      });

      request(
        `conversations/${conversationId}`,
        'DELETE',
        {},
        {
          Authorization: `Bearer ${accessToken}`,
        },
      )
        .then(response => {
          if (response.status === 200) {
            dispatch({
              type: 'END_CONVERSATION_SUCCESS',
              payload: { conversationId },
            });
            resolve(response.data);
          } else {
            dispatch({
              type: 'END_CONVERSATION_ERROR',
              payload: {
                conversationId,
                error: response.message,
              },
            });
            reject(response);
          }
        })
        .catch(error => {
          dispatch({
            type: 'END_CONVERSATION_ERROR',
            payload: {
              conversationId,
              error: error.message,
            },
          });
          reject(error);
        });
    });
}



// WEBPACK FOOTER //
// ./src/actions/messaging.js